/* C.5 — Company Repository: CSV Import UI (V1).
 *
 * Frozen scope (operator CR, 2026-02):
 *   - Reuses existing POST /api/data/admin/import endpoint — no backend
 *     changes, no changes to the importer's validation rules.
 *   - Required CSV columns: isin, company_name. Optional columns
 *     supported as today (nse_symbol, bse_code, identifier_type,
 *     identifier_value, sector, corporate_domain, asset_type, tier,
 *     active, logo_filename, logo_format, logo_source, reedos_comp_id).
 *   - Provides a Download Sample CSV link (pure client-side Blob — no
 *     extra backend endpoint).
 *   - Displays import summary: Rows Processed, Inserted, Updated,
 *     Skipped, Errors.
 *   - When errors > 0, exposes a Download Error Report button that
 *     emits a CSV of (line_no, isin, message) rows from the importer's
 *     errors[] response.
 *   - Out-of-scope: column mapping, data transformation, preview
 *     wizard, scheduling, approval workflow.
 *
 * Parent ``CompanyRepository.jsx`` is responsible for the data:import
 * permission gate and for remounting this modal on each open (via the
 * ``key`` prop) so ``useState`` initialises fresh — same pattern used
 * by ``CompanyCreateModal``.
 */

import { useRef, useState } from "react";
import { X, DownloadSimple, FileArrowUp, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

// Header columns the importer recognises today. Required columns are
// the first two; the rest are optional. Kept in sync with the backend's
// `REQUIRED_COLUMNS` + supported-optional set in routes_data_admin.py.
const SAMPLE_HEADER = [
    "isin", "company_name", "nse_symbol", "bse_code",
    "identifier_type", "identifier_value",
    "sector", "corporate_domain", "asset_type", "tier",
    "active",
    "logo_filename", "logo_format", "logo_source", "reedos_comp_id",
];

// One illustrative row so the operator can see the expected shape for
// each column. Uses well-known canonical values (TCS) so the sample is
// instantly recognisable.
const SAMPLE_ROW = [
    "INE467B01029", "Tata Consultancy Services Limited",
    "TCS", "532540",
    "", "",
    "IT Services", "tcs.com", "LISTED_EQUITY", "TIER_1",
    "true",
    "", "", "", "",
];

function csvEscape(v) {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function rowsToCsv(rows) {
    return rows.map((r) => r.map(csvEscape).join(",")).join("\n") + "\n";
}

function triggerDownload(filename, contents, mime = "text/csv;charset=utf-8") {
    const blob = new Blob([contents], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 250);
}

function downloadSampleTemplate() {
    triggerDownload(
        "company-repository-template.csv",
        rowsToCsv([SAMPLE_HEADER, SAMPLE_ROW]),
    );
}

function downloadErrorReport(report) {
    const header = ["line_no", "isin", "error"];
    const errors = report?.errors || [];
    if (errors.length === 0) return;
    const rows = [header, ...errors.map((e) => [
        // Backend's ImportError uses ``line`` (not ``line_no``) and
        // ``error`` (not ``message``); coalesce both spellings so this
        // download keeps working if either side renames.
        e.line ?? e.line_no ?? "",
        e.isin ?? "",
        e.error ?? e.message ?? "",
    ])];
    const ts = (report?.finished_at || new Date().toISOString())
        .replace(/[:.]/g, "-");
    triggerDownload(`import-errors-${ts}.csv`, rowsToCsv(rows));
}


export default function CompanyImportCsvModal({ open, onClose, onImported }) {
    // Parent remounts via ``key={open}`` so the freshly-mounted state
    // is always empty; we don't need an in-effect reset.
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState(null);  // ImportReport or null
    const fileInputRef = useRef(null);

    if (!open) return null;

    const onFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        // Reset any prior import report when the operator picks a new file
        // so the result panel doesn't show stale data alongside a new
        // selection.
        setFile(f);
        if (report) setReport(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file || busy) return;
        setBusy(true);
        setReport(null);
        try {
            const form = new FormData();
            form.append("file", file);
            // Explicit undefined Content-Type lets axios derive the
            // ``multipart/form-data; boundary=…`` value from the
            // FormData payload — without this override, our shared
            // ``api`` instance's default ``Content-Type: application/json``
            // header sticks to the request and the server rejects the
            // upload (or worse, accepts it as JSON garbage).
            const { data } = await api.post("/data/admin/import", form, {
                headers: { "Content-Type": undefined },
            });
            setReport(data);
            const summary = `Inserted ${data.inserted} · Updated ${data.updated} · Skipped ${data.skipped}` +
                (data.errors?.length ? ` · ${data.errors.length} error${data.errors.length === 1 ? "" : "s"}` : "");
            if (data.errors?.length) {
                toast.warning(`Import finished with errors — ${summary}`);
            } else {
                toast.success(`Import complete — ${summary}`);
            }
            onImported && onImported(data);
        } catch (err) {
            toast.error(formatApiError(err, "CSV import failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="company-import-modal-backdrop"
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose && onClose(); }}
        >
            <div
                data-testid="company-import-modal"
                className="bg-white rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Data</div>
                        <h2 className="text-base font-semibold text-slate-900">Import Companies (CSV)</h2>
                    </div>
                    <button
                        type="button"
                        data-testid="company-import-modal-close"
                        onClick={() => !busy && onClose && onClose()}
                        className="text-slate-400 hover:text-slate-900 transition-colors"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-5">
                    {/* ───── Instructions / template download ───── */}
                    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 space-y-2">
                        <div className="text-xs text-slate-700">
                            <div className="font-semibold text-slate-900 mb-1">CSV format</div>
                            <ul className="list-disc ml-4 space-y-0.5 text-[12px] text-slate-600">
                                <li>Required columns: <code className="text-[11px] bg-white px-1 py-0.5 rounded border border-slate-200">isin</code>, <code className="text-[11px] bg-white px-1 py-0.5 rounded border border-slate-200">company_name</code></li>
                                <li>Matching uses ISIN first, then NSE symbol as a fallback for legacy rows.</li>
                                <li>Existing rows are <strong>updated</strong>; new rows are <strong>inserted</strong>; malformed rows are <strong>skipped</strong> with line-level errors.</li>
                            </ul>
                        </div>
                        <button
                            type="button"
                            data-testid="company-import-download-template"
                            onClick={downloadSampleTemplate}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900 underline underline-offset-2"
                        >
                            <DownloadSimple size={14} />
                            Download sample CSV template
                        </button>
                    </div>

                    {/* ───── File picker ───── */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-slate-600">CSV file <span className="text-rose-500">*</span></label>
                        <div className="flex items-center gap-3">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                data-testid="company-import-file-input"
                                onChange={onFileChange}
                                disabled={busy}
                                className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 file:bg-white file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50 disabled:opacity-50"
                            />
                        </div>
                        {file && (
                            <div data-testid="company-import-file-name" className="text-[11px] text-slate-500">
                                {file.name} · {(file.size / 1024).toFixed(1)} KB
                            </div>
                        )}
                    </div>

                    {/* ───── Import summary (post-submit) ───── */}
                    {report && (
                        <div data-testid="company-import-report" className="rounded-md border border-slate-200 bg-white px-4 py-3">
                            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Import summary</div>
                            <div className="grid grid-cols-6 gap-3 text-center">
                                <Stat label="Rows" value={report.rows_seen} tone="slate" testid="company-import-rows-seen" />
                                <Stat label="Inserted" value={report.inserted} tone="emerald" testid="company-import-inserted" />
                                <Stat label="Updated" value={report.updated} tone="sky" testid="company-import-updated" />
                                <Stat
                                    label="Overwrites Existing"
                                    value={report.overwrites_existing ?? report.updated ?? 0}
                                    tone="amber-strong"
                                    testid="company-import-overwrites"
                                />
                                <Stat label="Skipped" value={report.skipped} tone="amber" testid="company-import-skipped" />
                                <Stat label="Errors" value={report.errors?.length || 0} tone="rose" testid="company-import-errors" />
                            </div>
                            {(report.overwritten_isins?.length || 0) > 0 && (
                                <details
                                    data-testid="company-import-overwritten-detail"
                                    className="mt-3 pt-3 border-t border-slate-100 text-[12px]"
                                >
                                    <summary className="cursor-pointer flex items-start gap-2 text-amber-800 hover:text-amber-900 select-none">
                                        <Warning size={14} className="mt-0.5 flex-shrink-0" />
                                        <span>
                                            {report.overwritten_isins.length}
                                            {report.overwrites_existing > report.overwritten_isins.length ? "+" : ""}
                                            {" "}existing row{report.overwritten_isins.length === 1 ? " was" : "s were"} overwritten — click to view ISINs
                                        </span>
                                    </summary>
                                    <div className="mt-2 pl-5 font-mono text-[11px] text-slate-600 break-all leading-relaxed">
                                        {report.overwritten_isins.join(", ")}
                                        {report.overwrites_existing > report.overwritten_isins.length && (
                                            <span className="text-slate-400"> &middot; +{report.overwrites_existing - report.overwritten_isins.length} more</span>
                                        )}
                                    </div>
                                </details>
                            )}
                            {report.errors?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                                    <div className="flex items-start gap-2 text-[12px] text-rose-700">
                                        <Warning size={14} className="mt-0.5 flex-shrink-0" />
                                        <span>
                                            {report.errors.length} row{report.errors.length === 1 ? "" : "s"} could not be processed.
                                            Download the error report for line-level details.
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="company-import-download-errors"
                                        onClick={() => downloadErrorReport(report)}
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 hover:text-rose-900 underline underline-offset-2 whitespace-nowrap"
                                    >
                                        <DownloadSimple size={14} />
                                        Download error report
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ───── Action row ───── */}
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <Button
                            type="button"
                            variant="outline"
                            data-testid="company-import-cancel"
                            disabled={busy}
                            onClick={() => onClose && onClose()}
                        >{report ? "Close" : "Cancel"}</Button>
                        <Button
                            type="submit"
                            data-testid="company-import-submit"
                            disabled={!file || busy}
                            className="gap-2"
                        >
                            <FileArrowUp size={14} />
                            {busy ? "Importing…" : "Upload & Import"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}


// Small subcomponent for the 5-up summary tiles. Kept inline since it
// has no reuse outside this modal.
function Stat({ label, value, tone, testid }) {
    const toneClass = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        sky: "text-sky-700",
        amber: "text-amber-700",
        // Stronger amber for "Overwrites Existing" — operator-CR signal
        // that the upload touched (and rewrote) live corpus records.
        "amber-strong": value > 0 ? "text-amber-800" : "text-slate-400",
        rose: value > 0 ? "text-rose-700" : "text-slate-400",
    }[tone] || "text-slate-900";
    return (
        <div className="px-1">
            <div data-testid={testid} className={`text-xl font-semibold ${toneClass}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5 leading-tight">{label}</div>
        </div>
    );
}
